import React from 'react';
import { Cookie, Shield, Settings, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function CookiePolicy() {
  return (
    <>
      <SEO
        title="Cookie Policy | TacticalSolutions.ai"
        description="Learn about how TacticalSolutions.ai uses cookies and similar technologies to improve your browsing experience and comply with PECR regulations."
        keywords={[
          'Cookie Policy',
          'Cookies',
          'PECR Compliance',
          'Privacy',
          'Tracking Technologies'
        ]}
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <Cookie className="h-16 w-16 text-teal-500" />
            </div>
            <h1 className="text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-purple-400">
              Cookie Policy
            </h1>
            <p className="text-gray-400">Last Updated: January 27, 2025</p>
          </div>

          <div className="max-w-3xl mx-auto mb-12">
            <div className="grid grid-cols-3 gap-6">
              <div className="glass-card p-6 rounded-xl backdrop-blur-md text-center">
                <Shield className="h-6 w-6 text-teal-500 mx-auto mb-3" />
                <span className="text-gray-300 text-sm">PECR Compliant</span>
              </div>
              <div className="glass-card p-6 rounded-xl backdrop-blur-md text-center">
                <Eye className="h-6 w-6 text-teal-500 mx-auto mb-3" />
                <span className="text-gray-300 text-sm">Transparent Usage</span>
              </div>
              <div className="glass-card p-6 rounded-xl backdrop-blur-md text-center">
                <Settings className="h-6 w-6 text-teal-500 mx-auto mb-3" />
                <span className="text-gray-300 text-sm">User Control</span>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div className="glass-card p-8 rounded-xl backdrop-blur-md">
              <h2 className="text-2xl font-bold text-white mb-6">1. What Are Cookies?</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.
                </p>
                <p>
                  This Cookie Policy explains what cookies are, how we use them, the types of cookies we use, and how you can control or delete them.
                </p>
              </div>
            </div>

            <div className="glass-card p-8 rounded-xl backdrop-blur-md">
              <h2 className="text-2xl font-bold text-white mb-6">2. How We Use Cookies</h2>
              <div className="text-gray-300 space-y-4">
                <p>We use cookies for the following purposes:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>To enable essential website functionality and security features</li>
                  <li>To remember your preferences and settings</li>
                  <li>To analyze how visitors use our website and improve our services</li>
                  <li>To measure the effectiveness of our marketing campaigns</li>
                  <li>To protect against spam and abuse on our contact forms</li>
                </ul>
              </div>
            </div>

            <div className="glass-card p-8 rounded-xl backdrop-blur-md">
              <h2 className="text-2xl font-bold text-white mb-6">3. Types of Cookies We Use</h2>

              <div className="space-y-6">
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="text-xl font-semibold text-green-400 mb-3">3.1 Strictly Necessary Cookies</h3>
                  <p className="text-gray-300 mb-2">
                    These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and accessibility.
                  </p>
                  <div className="bg-gray-800/50 rounded-lg p-4 mt-3">
                    <p className="text-sm text-gray-400 mb-2"><strong className="text-gray-300">Examples:</strong></p>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• Session cookies for secure browsing</li>
                      <li>• Cookie consent preferences</li>
                      <li>• Security and fraud prevention</li>
                    </ul>
                  </div>
                  <p className="text-sm text-teal-400 mt-2">
                    These cookies cannot be disabled as the website would not function properly without them.
                  </p>
                </div>

                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="text-xl font-semibold text-blue-400 mb-3">3.2 Analytics Cookies</h3>
                  <p className="text-gray-300 mb-2">
                    These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
                  </p>
                  <div className="bg-gray-800/50 rounded-lg p-4 mt-3">
                    <p className="text-sm text-gray-400 mb-2"><strong className="text-gray-300">Purpose:</strong></p>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• Measure website traffic and usage patterns</li>
                      <li>• Identify popular content and pages</li>
                      <li>• Improve website performance and user experience</li>
                      <li>• Test new features and functionality</li>
                    </ul>
                  </div>
                  <p className="text-sm text-blue-400 mt-2">
                    You can opt out of these cookies through your cookie preferences.
                  </p>
                </div>

                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-xl font-semibold text-purple-400 mb-3">3.3 Functional Cookies</h3>
                  <p className="text-gray-300 mb-2">
                    These cookies enable the website to provide enhanced functionality and personalization based on your previous visits.
                  </p>
                  <div className="bg-gray-800/50 rounded-lg p-4 mt-3">
                    <p className="text-sm text-gray-400 mb-2"><strong className="text-gray-300">Examples:</strong></p>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• Remember your preferences and settings</li>
                      <li>• Maintain your session across pages</li>
                      <li>• Remember previously filled form data</li>
                      <li>• Provide chat widget functionality</li>
                    </ul>
                  </div>
                </div>

                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="text-xl font-semibold text-orange-400 mb-3">3.4 Marketing and Targeting Cookies</h3>
                  <p className="text-gray-300 mb-2">
                    These cookies track your browsing activity to display relevant advertisements and measure campaign effectiveness.
                  </p>
                  <div className="bg-gray-800/50 rounded-lg p-4 mt-3">
                    <p className="text-sm text-gray-400 mb-2"><strong className="text-gray-300">Purpose:</strong></p>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• Display relevant advertisements on other websites</li>
                      <li>• Measure advertising campaign effectiveness</li>
                      <li>• Limit the number of times you see an advertisement</li>
                      <li>• Understand your interests to provide relevant content</li>
                    </ul>
                  </div>
                  <p className="text-sm text-orange-400 mt-2">
                    We only use these cookies with your explicit consent.
                  </p>
                </div>
              </div>
            </div>

            <div className="glass-card p-8 rounded-xl backdrop-blur-md">
              <h2 className="text-2xl font-bold text-white mb-6">4. Third-Party Cookies</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  Some cookies on our website are placed by third-party services that appear on our pages. We use the following third-party services:
                </p>

                <div className="space-y-4 mt-4">
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Google reCAPTCHA</h4>
                    <p className="text-sm text-gray-400 mb-2">
                      Used to protect our contact forms from spam and automated abuse. This service may set cookies and collect information about your browser and device.
                    </p>
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-teal-400 hover:text-teal-300"
                    >
                      Google Privacy Policy →
                    </a>
                  </div>

                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Netlify</h4>
                    <p className="text-sm text-gray-400 mb-2">
                      Our hosting provider may use cookies for security, performance monitoring, and analytics purposes.
                    </p>
                    <a
                      href="https://www.netlify.com/privacy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-teal-400 hover:text-teal-300"
                    >
                      Netlify Privacy Policy →
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-8 rounded-xl backdrop-blur-md">
              <h2 className="text-2xl font-bold text-white mb-6">5. How to Control and Delete Cookies</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in our cookie consent banner when you first visit our website.
                </p>

                <h3 className="text-xl font-semibold text-teal-400 mt-6 mb-3">Cookie Consent Manager</h3>
                <p>
                  When you first visit our website, you'll see a cookie consent banner. You can choose to:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Accept all cookies</li>
                  <li>Reject non-essential cookies</li>
                  <li>Customize your cookie preferences by category</li>
                </ul>

                <h3 className="text-xl font-semibold text-teal-400 mt-6 mb-3">Browser Settings</h3>
                <p>
                  Most web browsers allow you to control cookies through their settings. You can set your browser to refuse cookies or delete certain cookies. Here's how to manage cookies in popular browsers:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>
                    <strong>Google Chrome:</strong>{' '}
                    <a
                      href="https://support.google.com/chrome/answer/95647"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-400 hover:text-teal-300"
                    >
                      Chrome Cookie Settings
                    </a>
                  </li>
                  <li>
                    <strong>Mozilla Firefox:</strong>{' '}
                    <a
                      href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-400 hover:text-teal-300"
                    >
                      Firefox Cookie Settings
                    </a>
                  </li>
                  <li>
                    <strong>Safari:</strong>{' '}
                    <a
                      href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-400 hover:text-teal-300"
                    >
                      Safari Cookie Settings
                    </a>
                  </li>
                  <li>
                    <strong>Microsoft Edge:</strong>{' '}
                    <a
                      href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-400 hover:text-teal-300"
                    >
                      Edge Cookie Settings
                    </a>
                  </li>
                </ul>

                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mt-4">
                  <p className="text-yellow-300 text-sm">
                    <strong>Please note:</strong> If you disable or refuse cookies, some parts of our website may become inaccessible or not function properly.
                  </p>
                </div>
              </div>
            </div>

            <div className="glass-card p-8 rounded-xl backdrop-blur-md">
              <h2 className="text-2xl font-bold text-white mb-6">6. Cookie Duration</h2>
              <div className="text-gray-300 space-y-4">
                <p>Cookies can be either "session" cookies or "persistent" cookies:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>
                    <strong className="text-teal-400">Session Cookies:</strong> Temporary cookies that are deleted when you close your browser
                  </li>
                  <li>
                    <strong className="text-teal-400">Persistent Cookies:</strong> Remain on your device until they expire or you delete them. Duration varies by cookie type and purpose.
                  </li>
                </ul>
              </div>
            </div>

            <div className="glass-card p-8 rounded-xl backdrop-blur-md">
              <h2 className="text-2xl font-bold text-white mb-6">7. Updates to This Cookie Policy</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by updating the "Last Updated" date at the top of this policy.
                </p>
              </div>
            </div>

            <div className="glass-card p-8 rounded-xl backdrop-blur-md">
              <h2 className="text-2xl font-bold text-white mb-6">8. Contact Us</h2>
              <div className="text-gray-300 space-y-4">
                <p>
                  If you have any questions about our use of cookies or this Cookie Policy, please contact us:
                </p>
                <div className="mt-4 space-y-2">
                  <p><strong className="text-teal-400">Email:</strong> privacy@tacticalsolutions.ai</p>
                  <p><strong className="text-teal-400">DPO:</strong> dpo@tacticalsolutions.ai</p>
                  <p><strong className="text-teal-400">Address:</strong> London, United Kingdom</p>
                </div>
              </div>
            </div>

            <div className="glass-card p-8 rounded-xl backdrop-blur-md">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-white mb-6">More Information</h2>
                <p className="text-gray-300 mb-8">
                  For more information about how we process your personal data, please see our Privacy Policy.
                </p>
                <Link
                  to="/legal/privacy-policy"
                  className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 transition-all duration-200"
                >
                  View Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
